import React from "react";
import { Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { useSiteAssets } from "@/lib/siteAssets";
import { useSiteContent } from "@/lib/siteContent";
import { imageUrl } from "@/components/ImageUpload";
import { ArrowRight, CheckCircle2, Building2, Stethoscope, Calendar, Mail, ShieldCheck, BarChart3 } from "lucide-react";
import { SEOHead, JsonLd } from "@/components/SEO";

const Stat = ({ value, label }) => (
    <div>
        <div className="font-heading text-3xl md:text-4xl font-extrabold text-brand-forest">{value}</div>
        <div className="text-xs uppercase tracking-wider text-brand-mute mt-1">{label}</div>
    </div>
);

export default function Landing() {
    const assets = useSiteAssets();
    const content = useSiteContent();
    const HERO_IMG = imageUrl(assets.landing_hero);
    const ABOUT_IMG = imageUrl(assets.landing_about);
    const ABSTRACT = imageUrl(assets.landing_abstract);
    return (
        <Shell>
            <SEOHead
                title="Vendors for corporate health fairs — find, vet, and book in one place"
                description="HealthFairX is the B2B marketplace where corporate hosts find vetted healthcare and wellness vendors for employee health fairs. Notification-driven matching, secure payments, and verified host reviews."
            />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "Organization",
                name: "HealthFairX",
                url: typeof window !== "undefined" ? window.location.origin : undefined,
                logo: assets?.brand_logo ? imageUrl(assets.brand_logo) : undefined,
                description: "B2B marketplace connecting corporate hosts with healthcare and wellness vendors for employee health fairs.",
            }} />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "WebSite",
                name: "HealthFairX",
                url: typeof window !== "undefined" ? window.location.origin : undefined,
                potentialAction: {
                    "@type": "SearchAction",
                    target: (typeof window !== "undefined" ? window.location.origin : "") + "/vendors?q={search_term_string}",
                    "query-input": "required name=search_term_string",
                },
            }} />
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 grain opacity-50" aria-hidden />
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 md:pt-20 pb-16 md:pb-28 grid lg:grid-cols-12 gap-10 items-center relative">
                    <div className="lg:col-span-7 animate-fade-up">
                        <div className="chip mb-6" data-testid="hero-badge">
                            <span className="h-1.5 w-1.5 rounded-full bg-brand-coral mr-2" /> Free for hosting companies
                        </div>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
                            Run a health fair your <span className="text-brand-coral italic">people</span><br />
                            actually remember.
                        </h1>
                        <p className="mt-6 text-lg text-brand-mute max-w-xl leading-relaxed">
                            HealthFairX connects corporate hosts with curated healthcare & wellness vendors — discovery, scheduling, and coordination, in one calm workflow.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link data-testid="hero-host-cta" to="/register?role=host_company" className="pill btn-coral inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold">
                                Host a fair <ArrowRight size={16} />
                            </Link>
                            <Link data-testid="hero-vendor-cta" to="/register?role=vendor" className="pill inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold border border-brand-line bg-white hover:bg-brand-bone">
                                Join as a vendor
                            </Link>
                            <Link data-testid="hero-browse" to="/marketplace" className="pill inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-brand-forest hover:bg-brand-mint">
                                Browse events
                            </Link>
                        </div>
                        <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
                            <Stat value={content.landing_stat_1_value} label={content.landing_stat_1_label} />
                            <Stat value={content.landing_stat_2_value} label={content.landing_stat_2_label} />
                            <Stat value={content.landing_stat_3_value} label={content.landing_stat_3_label} />
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="absolute -top-6 -right-6 h-40 w-40 rounded-full bg-brand-coral/20 blur-3xl" aria-hidden />
                        <div className="absolute -bottom-8 -left-8 h-40 w-40 rounded-full bg-brand-forest/20 blur-3xl" aria-hidden />
                        <div className="relative rounded-3xl overflow-hidden border border-brand-line bg-white">
                            <img src={HERO_IMG} alt="Modern corporate atrium with health fair" className="w-full h-[420px] object-cover" />
                        </div>
                        <div className="hidden md:block absolute -bottom-6 -left-6 bg-white rounded-2xl border border-brand-line p-4 shadow-lg w-64">
                            <div className="text-xs uppercase tracking-wider text-brand-mute">Next event</div>
                            <div className="font-heading text-lg font-bold mt-1">Acme HQ — Annual Fair</div>
                            <div className="text-sm text-brand-mute mt-1">12 vendors confirmed</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" className="section-pad bg-white border-y border-brand-line">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
                        <div className="md:col-span-7">
                            <div className="label-base">How it works</div>
                            <h2 className="font-heading text-3xl md:text-5xl font-extrabold tracking-tight">Plan your health fair in four simple steps.</h2>
                        </div>
                        <p className="md:col-span-5 text-brand-mute text-base">We make it easy to connect with qualified wellness vendors so you can focus on creating a great employee experience.</p>
                    </div>
                    <div className="grid md:grid-cols-4 gap-6">
                        {[
                            { icon: Calendar, title: content.hiw_step_1_title || "Tell us about your event", body: content.hiw_step_1_body || "Share your date, location, expected attendance, and the wellness services you're looking for." },
                            { icon: ShieldCheck, title: content.hiw_step_2_title || "Reviewed by our team", body: content.hiw_step_2_body || "We verify your requirements and prepare your event for qualified vendor matching." },
                            { icon: Mail, title: content.hiw_step_3_title || "Qualified vendors are invited", body: content.hiw_step_3_body || "We notify vetted vendors that match your event needs and service categories." },
                            { icon: CheckCircle2, title: content.hiw_step_4_title || "Choose your vendors", body: content.hiw_step_4_body || "Review interested providers, make your selections, and enjoy your event." },
                        ].map((s, i) => (
                            <div key={i} className="rounded-xl border border-brand-line bg-brand-bone p-6 card-hover" data-testid={`how-step-${i}`}>
                                <div className="h-10 w-10 rounded-full bg-brand-forest text-white inline-flex items-center justify-center mb-4">
                                    <s.icon size={18} />
                                </div>
                                <div className="text-xs font-bold text-brand-coral mb-1">STEP {i + 1}</div>
                                <h3 className="font-heading text-lg font-bold">{s.title}</h3>
                                <p className="text-sm text-brand-mute mt-2 leading-relaxed">{s.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TWO COLUMNS */}
            <section className="section-pad">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
                    <div className="rounded-3xl bg-brand-forest text-white p-10 md:p-14 relative overflow-hidden" data-testid="hosts-block">
                        <Building2 className="absolute -right-4 -bottom-4 opacity-10" size={200} />
                        <div className="text-xs uppercase tracking-widest text-brand-coral mb-3">For host companies</div>
                        <h3 className="font-heading text-3xl md:text-4xl font-extrabold text-white">Run it once. Reuse forever.</h3>
                        <ul className="mt-6 space-y-3 text-white/90">
                            {["100% free to host", "Curated, pre-vetted vendor pool", "Automated approvals + comms", "See your confirmed lineup at a glance"].map((t, i) => (
                                <li key={i} className="flex items-start gap-3"><CheckCircle2 size={18} className="text-brand-coral shrink-0 mt-0.5" /> <span>{t}</span></li>
                            ))}
                        </ul>
                        <Link to="/register?role=host_company" className="mt-8 inline-flex items-center gap-2 pill bg-white text-brand-forest px-6 py-3 font-semibold hover:bg-brand-bone">
                            Create your first event <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="rounded-3xl bg-white border border-brand-line p-10 md:p-14 relative overflow-hidden" data-testid="vendors-block">
                        <Stethoscope className="absolute -right-4 -bottom-4 text-brand-coral/10" size={200} />
                        <div className="text-xs uppercase tracking-widest text-brand-coral mb-3">For vendors</div>
                        <h3 className="font-heading text-3xl md:text-4xl font-extrabold">Meet local employees.</h3>
                        <ul className="mt-6 space-y-3 text-brand-mute">
                            {["Set your service area and categories once", "Get notified the moment a fit appears", "One-click application + transparent pricing", "Only commit when approved"].map((t, i) => (
                                <li key={i} className="flex items-start gap-3"><CheckCircle2 size={18} className="text-brand-forest shrink-0 mt-0.5" /> <span>{t}</span></li>
                            ))}
                        </ul>
                        <Link to="/register?role=vendor" className="mt-8 inline-flex items-center gap-2 pill btn-coral px-6 py-3 font-semibold">
                            Create your booth profile <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* WELLNESS BANNER */}
            <section className="px-6 lg:px-10 pb-20">
                <div className="max-w-7xl mx-auto rounded-3xl border border-brand-line overflow-hidden grid md:grid-cols-2">
                    <div className="p-10 md:p-14 bg-brand-bone">
                        <div className="label-base">Why it matters</div>
                        <h3 className="font-heading text-3xl md:text-4xl font-extrabold leading-tight">Healthier teams, measured.</h3>
                        <p className="mt-4 text-brand-mute leading-relaxed">Companies that host annual health fairs see meaningful lifts in benefits engagement, preventive screenings, and retention. HealthFairX makes vendor discovery effortless so you can focus on the impact.</p>
                        <div className="mt-6 grid grid-cols-2 gap-4">
                            <div className="rounded-xl bg-white border border-brand-line p-5"><BarChart3 size={20} className="text-brand-coral" /><div className="mt-2 text-2xl font-bold font-heading">+38%</div><div className="text-xs text-brand-mute">benefits sign-ups</div></div>
                            <div className="rounded-xl bg-white border border-brand-line p-5"><CheckCircle2 size={20} className="text-brand-forest" /><div className="mt-2 text-2xl font-bold font-heading">9 in 10</div><div className="text-xs text-brand-mute">employees attend</div></div>
                        </div>
                    </div>
                    <img src={ABOUT_IMG} alt="" className="w-full h-full object-cover min-h-[320px]" />
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 lg:px-10 pb-24">
                <div className="max-w-7xl mx-auto rounded-3xl relative overflow-hidden bg-brand-forest text-white p-12 md:p-20">
                    <img src={ABSTRACT} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                    <div className="relative">
                        <h3 className="font-heading text-4xl md:text-5xl font-extrabold max-w-2xl tracking-tight text-white">Bring your team's wellness to life.</h3>
                        <p className="mt-4 text-white/80 max-w-xl">Spin up your first health fair in under ten minutes. Free for hosts, always.</p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link data-testid="cta-host" to="/register?role=host_company" className="pill btn-coral px-6 py-3 font-semibold">Host a fair</Link>
                            <Link data-testid="cta-vendor" to="/register?role=vendor" className="pill bg-white text-brand-forest px-6 py-3 font-semibold hover:bg-brand-bone">Become a vendor</Link>
                        </div>
                    </div>
                </div>
            </section>
        </Shell>
    );
}
